import { useQuery } from "@apollo/client/react";
import { Stack, styled, useMediaQuery } from "@mui/material";
import { useState } from "react";
import UserPreferences from "../../constants/userPreferences";
import useAuth from "../../context/UserContext";
import { GetSuspendedCredentialCountDocument } from "../../graphql/types";
import type { GetSuspendedCredentialCountQuery, GetSuspendedCredentialCountQueryVariables } from "../../graphql/types";
import { usePreferences } from "../../hooks/usePreferences";
import { CollapsedNavBar } from "./CollapsedNavBar";
import { ExpandedNavBar } from "./ExpandedNavBar";

const LOGO_HEIGHT = "110px";
const TOGGLE_NAVBAR_HEIGHT = "56px";

interface NavBarContainerProps {
  collapse: boolean;
}

const NavBarContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "collapse",
})<NavBarContainerProps>(({ theme, collapse }) => ({
  width: collapse ? "80px" : "263px",
  height: "100vh",
  backgroundColor: theme.palette.surfaceLight10.main,
  color: "text40.main",
  transition: "width 0.2s",
  borderRight: `1px solid ${theme.palette.surfaceLight40.main}`,
}));
const NavBar = () => {
  const { user } = useAuth();
  const { savePreference } = usePreferences({
    preferenceToFetch: UserPreferences.PrefersMiniNavbar,
  });
  const mini = (user?.userPreferences && user?.userPreferences[UserPreferences.PrefersMiniNavbar]) ?? false;
  const isSmallSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [collapse, setCollapse] = useState(mini || isSmallSize);
  const [prevMini, setPrevMini] = useState(mini);
  const [prevIsSmallSize, setPrevIsSmallSize] = useState(isSmallSize);
  if (prevMini !== mini || prevIsSmallSize !== isSmallSize) {
    setPrevMini(mini);
    setPrevIsSmallSize(isSmallSize);
    setCollapse(isSmallSize || mini);
  }
  const toggleMiniNavbar = () => {
    if (isSmallSize) {
      setCollapse(!collapse);
    } else {
      savePreference(UserPreferences.PrefersMiniNavbar, !mini);
      setCollapse(!mini);
    }
  };
  const { data: suspendedKeysData } = useQuery<
    GetSuspendedCredentialCountQuery,
    GetSuspendedCredentialCountQueryVariables
  >(GetSuspendedCredentialCountDocument, {
    fetchPolicy: "cache-and-network",
  });
  return (
    <NavBarContainer direction="column" collapse={collapse}>
      {collapse ? (
        <CollapsedNavBar
          toggleMiniNavbar={toggleMiniNavbar}
          logoHeight={LOGO_HEIGHT}
          toggleNavbarHeight={TOGGLE_NAVBAR_HEIGHT}
          suspendedKeysCount={suspendedKeysData?.getSuspendedCredentialCount || 0}
        />
      ) : (
        <ExpandedNavBar
          toggleMiniNavbar={toggleMiniNavbar}
          logoHeight={LOGO_HEIGHT}
          toggleNavbarHeight={TOGGLE_NAVBAR_HEIGHT}
          suspendedKeysCount={suspendedKeysData?.getSuspendedCredentialCount || 0}
        />
      )}
    </NavBarContainer>
  );
};

export { NavBar };
